import React, { useState, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import allLocales from '@fullcalendar/core/locales-all';
import { tasksApi } from '../services/api';
import { useDateTime } from '../contexts/DateTimeContext';
import { Button } from './ui/button';
import { useTask } from '../contexts/TaskContext';
import {
  ChevronLeft, ChevronRight, Plus
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDefaultFields } from '../lib/viewFields';
import { occurrenceEvents, useCalendarOccurrences } from '../lib/occurrences';

const priorityColors = {
  p1: { bg: '#DC2626', border: '#B91C1C', text: '#FFFFFF' },
  p2: { bg: '#F59E0B', border: '#D97706', text: '#000000' },
  p3: { bg: '#3B82F6', border: '#2563EB', text: '#FFFFFF' },
  p4: { bg: '#6B7280', border: '#4B5563', text: '#FFFFFF' },
};

const ProjectCalendar = ({ tasks = [], projects = [], onUpdate, prefillProjectId = null, visibleFields = null, customFieldDefs = [] }) => {
  const { t } = useTranslation();
  const { formatDate, formatTime, formatDayOfWeek, timeFormat, timezone, toUTCISO, toLocalDateString, getTimeInUserTimezone, weekStartsOn, language } = useDateTime();
  // FullCalendar's day names and "+N more" in the app's language
  const calendarLocale = { zh: 'zh-cn', ta: 'ta-in' }[language] || language;

  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [dateRange, setDateRange] = useState('');
  const fields = useMemo(() => {
    if (Array.isArray(visibleFields)) return new Set(visibleFields);
    return new Set([...getDefaultFields('calendar'), ...customFieldDefs.map(d => `cf_${d.id}`)]);
  }, [visibleFields, customFieldDefs]);
  const show = (key) => fields.has(key);

  // TaskContext
  const { openTaskCreation, openTaskDetail } = useTask();

  const calendarRef = useRef(null);
  const { occurrences, onDatesSet } = useCalendarOccurrences();

  const getProjectById = useCallback((projectId) => {
    return projects.find(p => p.id === projectId);
  }, [projects]);

  // Convert tasks to FullCalendar events (and the coming occurrences of recurring ones)
  const getEvents = useCallback(() => {
    const coming = occurrenceEvents(occurrences, tasks, {
      colorsFor: (task) => priorityColors[task.priority] || priorityColors.p4,
      projectNameOf: (id) => getProjectById(id)?.name,
    });
    return tasks
      .filter(task => task.due_date || task.scheduled_start)
      .map(task => {
        const colors = priorityColors[task.priority] || priorityColors.p4;
        const projectName = getProjectById(task.project_id)?.name;

        // Use scheduled_start/end if available, otherwise use due_date
        const start = task.scheduled_start || task.due_date;
        const end = task.scheduled_end || (task.due_date ? new Date(new Date(task.due_date).getTime() + (task.duration || 60) * 60000).toISOString() : null);

        return {
          id: task.id,
          title: task.title,
          start: start,
          end: end,
          allDay: !task.scheduled_start,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          textColor: colors.text,
          extendedProps: {
            task: task,
            priority: task.priority,
            duration: task.duration,
            projectName: projectName
          }
        };
      })
      .concat(coming);
  }, [tasks, getProjectById, occurrences]);

  // Handle calendar date change
  const handleDatesSet = (dateInfo) => {
    onDatesSet(dateInfo);
    const start = dateInfo.start;
    const end = new Date(dateInfo.end);
    end.setDate(end.getDate() - 1);

    if (dateInfo.view.type === 'dayGridMonth') {
      const monthNames = [
        t('months.january'), t('months.february'), t('months.march'), t('months.april'),
        t('months.may'), t('months.june'), t('months.july'), t('months.august'),
        t('months.september'), t('months.october'), t('months.november'), t('months.december'),
      ];
      // The month shown, not the month of the first visible day (a grid starting on Monday
      // opens November 2026 on 26 October); its middle stays in it in any time zone.
      const { currentStart, currentEnd } = dateInfo.view;
      const month = new Date((currentStart.getTime() + currentEnd.getTime()) / 2);
      setDateRange(`${monthNames[month.getMonth()]} ${month.getFullYear()}`);
    } else if (dateInfo.view.type === 'timeGridDay') {
      setDateRange(`${formatDayOfWeek(start, 'short')} ${formatDate(start)}`);
    } else {
      const startStr = formatDate(start);
      const endStr = formatDate(end);
      setDateRange(`${startStr} - ${endStr}`);
    }
  };

  // Handle calendar select (click/drag to create)
  const handleDateSelect = (selectInfo) => {
    const start = selectInfo.start;
    const end = selectInfo.end;
    const durationMins = Math.round((end - start) / (1000 * 60));

    // Get time in user's configured timezone
    const startTime = getTimeInUserTimezone(start);
    const endTime = getTimeInUserTimezone(end);
    const startTimeStr = `${startTime.hours.toString().padStart(2, '0')}:${startTime.minutes.toString().padStart(2, '0')}`;
    const endTimeStr = `${endTime.hours.toString().padStart(2, '0')}:${endTime.minutes.toString().padStart(2, '0')}`;

    openTaskCreation({
      date: start,
      startTime: startTimeStr,
      endTime: endTimeStr,
      duration: durationMins > 0 ? durationMins : 60,
      projectId: prefillProjectId
    });

    selectInfo.view.calendar.unselect();
  };

  // Handle event click (view task detail)
  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.extendedProps.isOccurrence) {  // a coming occurrence: its series' task
      openTaskDetail(clickInfo.event.extendedProps.task.id);
      return;
    }
    const taskId = clickInfo.event.id;
    openTaskDetail(taskId);
  };

  // Handle event drag (move existing task)
  const handleEventDrop = async (dropInfo) => {
    const taskId = dropInfo.event.id;
    const newStart = dropInfo.event.start;
    const newEnd = dropInfo.event.end;

    try {
      const updateData = dropInfo.event.allDay
        ? { due_date: toLocalDateString(newStart) }
        : {
            scheduled_start: toUTCISO(newStart),
            scheduled_end: toUTCISO(newEnd) || toUTCISO(new Date(newStart.getTime() + 60 * 60000))
          };

      await tasksApi.update(taskId, updateData);
      onUpdate?.();
    } catch (err) {
      console.error('Failed to update task:', err);
      dropInfo.revert();
    }
  };

  // Handle event resize
  const handleEventResize = async (resizeInfo) => {
    const taskId = resizeInfo.event.id;
    const newStart = resizeInfo.event.start;
    const newEnd = resizeInfo.event.end;
    const newDuration = Math.round((newEnd - newStart) / (1000 * 60));

    try {
      await tasksApi.update(taskId, {
        scheduled_start: toUTCISO(newStart),
        scheduled_end: toUTCISO(newEnd),
        duration: newDuration
      });
      onUpdate?.();
    } catch (err) {
      console.error('Failed to resize task:', err);
      resizeInfo.revert();
    }
  };

  // Navigation
  const goToPrev = () => calendarRef.current?.getApi().prev();
  const goToNext = () => calendarRef.current?.getApi().next();
  const goToToday = () => calendarRef.current?.getApi().today();

  const changeView = (view) => {
    setCurrentView(view);
    calendarRef.current?.getApi().changeView(view);
  };

  // Force priority colors on month-view events (FullCalendar ignores backgroundColor in dayGrid without this)
  const handleEventDidMount = (info) => {
    if (info.view.type === 'dayGridMonth' && info.el) {
      const bgColor = info.event.backgroundColor;
      if (bgColor) {
        info.el.style.backgroundColor = bgColor;
        const textColor = info.event.textColor;
        if (textColor) info.el.style.color = textColor;
      }
    }
  };

  // Custom event content
  const renderEventContent = (eventInfo) => {
    const task = eventInfo.event.extendedProps.task;
    const duration = eventInfo.event.extendedProps.duration;
    const start = eventInfo.event.start;
    const end = eventInfo.event.end;
    const project = getProjectById(task?.project_id);

    if (eventInfo.view.type === 'dayGridMonth') {
      return (
        <div className="px-1 py-0.5 overflow-hidden">
          <div className="flex items-center gap-1">
            {show('project') && project && (
              <span
                className="w-1.5 h-1.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: project.color || '#6B7280' }}
              />
            )}
            <div className="font-medium text-xs truncate flex-1">{eventInfo.event.title}</div>
          </div>
        </div>
      );
    }

    const isShort = end && (end - start < 45 * 60 * 1000);

    return (
      <div className="px-1.5 py-1 overflow-hidden h-full flex flex-col">
        <div className="font-semibold text-xs truncate">{eventInfo.event.title}</div>
        {!isShort && (
          <>
            <div className="text-[10px] opacity-90 mt-0.5">
              {formatTime(start)} - {formatTime(end)}
              {show('duration') && ` (${t('ui_task.duration.minutes', { m: duration })})`}
            </div>
            {show('project') && project && (
              <div className="text-[10px] opacity-85 mt-0.5 flex items-center gap-1 truncate">
                <span className="w-1.5 h-1.5 rounded-sm flex-shrink-0" style={{ backgroundColor: project.color || '#6B7280' }} />
                <span className="truncate">{project.name}</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="h-[calc(100dvh-15rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] md:h-[calc(100vh-12rem)] flex flex-col">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        {/* Navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToPrev} className="px-2">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday} className="text-xs sm:text-sm">
            {t('common.today')}
          </Button>
          <Button variant="outline" size="sm" onClick={goToNext} className="px-2">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Date Range */}
        <div className="text-foreground font-semibold text-base lg:text-lg">
          {dateRange}
        </div>

        {/* View Buttons */}
        <div className="flex items-center gap-1">
          <Button
            variant={currentView === 'timeGridDay' ? 'default' : 'outline'}
            size="sm"
            onClick={() => changeView('timeGridDay')}
            className={`text-xs sm:text-sm ${currentView === 'timeGridDay' ? 'bg-amber-500 text-black hover:bg-amber-400' : ''}`}
          >
            {t('planner.day_view')}
          </Button>
          <Button
            variant={currentView === 'timeGridWeek' ? 'default' : 'outline'}
            size="sm"
            onClick={() => changeView('timeGridWeek')}
            className={`text-xs sm:text-sm ${currentView === 'timeGridWeek' ? 'bg-amber-500 text-black hover:bg-amber-400' : ''}`}
          >
            {t('planner.week_view')}
          </Button>
          <Button
            variant={currentView === 'dayGridMonth' ? 'default' : 'outline'}
            size="sm"
            onClick={() => changeView('dayGridMonth')}
            className={`text-xs sm:text-sm ${currentView === 'dayGridMonth' ? 'bg-amber-500 text-black hover:bg-amber-400' : ''}`}
          >
            {t('planner.month_view')}
          </Button>
          <Button
            onClick={() => openTaskCreation({ projectId: prefillProjectId })}
            size="sm"
            className="ml-2 bg-amber-500 text-black hover:bg-amber-400"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('common.add')}
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex-1 bg-card/50 rounded-xl border border-border overflow-hidden">
        <style>{`
          .fc {
            --fc-border-color: hsl(var(--border));
            --fc-button-bg-color: hsl(var(--accent));
            --fc-button-border-color: hsl(var(--border));
            --fc-button-hover-bg-color: hsl(var(--accent));
            --fc-button-hover-border-color: hsl(var(--border));
            --fc-button-active-bg-color: #F59E0B;
            --fc-button-active-border-color: #F59E0B;
            --fc-today-bg-color: rgba(245,158,11,0.08);
            --fc-page-bg-color: transparent;
            --fc-neutral-bg-color: transparent;
            --fc-list-event-hover-bg-color: hsl(var(--accent));
            height: 100%;
            color: hsl(var(--foreground) / 0.85);
          }
          .fc .fc-toolbar {
            display: none !important;
          }
          /* Force transparent backgrounds on all cells */
          .fc td, .fc th,
          .fc .fc-daygrid-day,
          .fc .fc-daygrid-day-frame,
          .fc .fc-timegrid-col,
          .fc .fc-timegrid-col-frame {
            background: transparent !important;
          }
          .fc .fc-daygrid-day.fc-day-other {
            background: hsl(var(--muted) / 0.4) !important;
          }
          /* Header & day number text */
          .fc .fc-col-header-cell-cushion {
            color: hsl(var(--muted-foreground));
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            text-decoration: none;
          }
          .fc .fc-daygrid-day-number {
            color: hsl(var(--foreground) / 0.75);
            font-size: 12px;
            text-decoration: none;
          }
          .fc .fc-daygrid-day.fc-day-other .fc-daygrid-day-number {
            color: hsl(var(--muted-foreground) / 0.6);
          }
          .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
            color: #F59E0B;
            font-weight: bold;
          }
          /* Time labels */
          .fc .fc-timegrid-slot-label-cushion {
            color: hsl(var(--muted-foreground));
            font-size: 11px;
          }
          .fc-theme-standard td, .fc-theme-standard th {
            border-color: hsl(var(--border));
          }
          .fc .fc-scrollgrid {
            border-color: hsl(var(--border));
          }
          /* Now indicator */
          .fc .fc-timegrid-now-indicator-line {
            border-color: #F59E0B;
          }
          .fc .fc-timegrid-now-indicator-arrow {
            border-color: #F59E0B;
            border-top-color: transparent;
            border-bottom-color: transparent;
          }
          /* Events */
          .fc-event {
            cursor: pointer;
            border-radius: 4px;
            border-width: 0 0 0 3px !important;
            font-size: 11px;
          }
          .fc-event:hover {
            opacity: 0.85;
          }
          .fc .fc-timegrid-event {
            border-radius: 4px;
          }
          .fc-h-event {
            border-width: 0 0 0 3px !important;
          }
          .fc-timegrid-slot {
            height: 2.5rem;
          }
          .fc .fc-highlight {
            background: rgba(245,158,11,0.2);
          }
          .fc-col-header-cell {
            padding: 8px 0;
          }
          .fc-daygrid-day-events {
            padding: 2px;
          }
          /* More-link */
          .fc .fc-daygrid-more-link {
            color: hsl(var(--muted-foreground));
            font-size: 11px;
          }
          .fc .fc-daygrid-more-link:hover {
            color: #F59E0B;
          }
          /* Popover */
          .fc .fc-popover {
            background: hsl(var(--popover));
            border: 1px solid hsl(var(--border));
            border-radius: 8px;
          }
          .fc .fc-popover-header {
            background: hsl(var(--muted));
            color: hsl(var(--foreground) / 0.85);
            border-radius: 7px 7px 0 0;
          }
          .fc .fc-popover-close {
            color: hsl(var(--muted-foreground));
          }
        `}</style>

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          timeZone={timezone}
          locales={allLocales}
          locale={calendarLocale}
          firstDay={weekStartsOn}
          headerToolbar={false}
          events={getEvents()}
          editable={true}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={3}
          weekends={true}
          nowIndicator={true}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          slotDuration="00:30:00"
          snapDuration="00:15:00"
          height="100%"
          select={handleDateSelect}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventContent={renderEventContent}
          eventDidMount={handleEventDidMount}
          eventResizableFromStart={true}
          datesSet={handleDatesSet}
          selectMinDistance={5}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: timeFormat === '12h'
          }}
        />
      </div>

    </div>
  );
};

export default ProjectCalendar;
